import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Toolbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Badge,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Menu,
  Divider,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Event,
  Person,
  LocationOn,
  Group,
  Gavel,
  OpenInNew,
  FilterList,
  Refresh,
  ExitToApp,
  AccountCircle,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useCalendarData } from '../hooks/useCalendarData';
import { useMsal, useAccount } from '@azure/msal-react';
import {
  getDaysInMonth,
  formatDate,
  isSameDay,
  isToday,
  getDateTypeColor,
} from '../utils/dateUtils';
import { MATTER_TYPE_CONFIGS, BIGTIME_LINKS, DATE_TYPE_COLORS } from '../constants';
import { Matter } from '../types';
import LoadingOverlay from './LoadingOverlay';

const CalendarDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const {
    matters,
    currentDate,
    selectedMatter,
    filters,
    isLoading,
    isError,
    error,
    consultants,
    clients,
    locations,
    setCurrentDate,
    setSelectedMatter,
    setFilters,
    refreshData,
    goToToday,
    goToNextMonth,
    goToPreviousMonth,
    getEventsForDate,
  } = useCalendarData();

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);

  const calendarWeeks = useMemo(() => {
    const days = getDaysInMonth(currentDate);
    const firstDay = days[0];
    const lastDay = days[days.length - 1];

    // Find the Sunday of the week containing the first day of the month
    const startDate = new Date(firstDay);
    const dayOfWeek = startDate.getDay();
    const sundayOffset = -dayOfWeek; // Sunday is 0, so we subtract the current day of week
    startDate.setDate(startDate.getDate() + sundayOffset);

    // Find the Saturday of the week containing the last day of the month
    const endDate = new Date(lastDay);
    const endDayOfWeek = endDate.getDay();
    const saturdayOffset = endDayOfWeek === 6 ? 0 : 6 - endDayOfWeek;
    endDate.setDate(endDate.getDate() + saturdayOffset);

    // Generate all days from start Sunday to end Saturday
    const allDays = [];
    const currentDay = new Date(startDate);

    while (currentDay <= endDate) {
      allDays.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }

    // Group days into weeks (7 days each)
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
      const week = allDays.slice(i, i + 7);
      // Only include weeks that contain at least one day from the current month
      const hasCurrentMonthDay = week.some(day => day.getMonth() === currentDate.getMonth());
      if (hasCurrentMonthDay) {
        weeks.push(week);
      }
    }

    return weeks;
  }, [currentDate]);

  const getTypeColor = (type: string) => {
    return MATTER_TYPE_CONFIGS[type as keyof typeof MATTER_TYPE_CONFIGS]?.color || '#607D8B';
  };

  const getEventColor = (event: any) => {
    return getDateTypeColor(event.dateType);
  };

  const getEventTextColor = (event: any) => {
    // Use black text for maize and vermilion backgrounds for better contrast
    const lightColors = ['additional', 'due'];
    return lightColors.includes(event.dateType) ? '#000000' : '#ffffff';
  };

  const handleRefresh = () => {
    refreshData();
    setSnackbarMessage('Calendar data refreshed');
    setSnackbarOpen(true);
  };

  const renderMatterCard = (matter: Matter, isCompact = false, uniqueKey?: string, event?: any) => (
    <Card
      key={uniqueKey || matter.id}
      sx={{
        mb: 0.5,
        cursor: 'pointer',
        borderLeft: `4px solid ${event ? getEventColor(event) : getTypeColor(matter.type)}`,
        '&:hover': { boxShadow: 2 },
        width: '100%',
        minWidth: 0,
      }}
      onClick={() => {
        setSelectedMatter(matter);
        setSelectedCalendarDate(event?.date || null);
      }}
    >
      <CardContent sx={{ p: isCompact ? 1 : 1.5, '&:last-child': { pb: isCompact ? 1 : 1.5 } }}>
        <Typography
          variant={isCompact ? 'caption' : 'body2'}
          fontWeight="bold"
          sx={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            lineHeight: 1.2,
          }}
        >
          {matter.displayName}
        </Typography>
        <Typography
          variant="caption"
          color="text.primary"
          display="block"
          sx={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            fontWeight: 400,
          }}
        >
          {matter.caseLocation}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={matter.type === 'Other' && matter.originalType
              ? matter.originalType
              : matter.type}
            size="small"
            sx={{
              bgcolor: getTypeColor(matter.type),
              color: 'white',
              fontSize: '0.65rem',
              height: 20,
              maxWidth: '100%',
              fontWeight: 600,
            }}
          />
          {matter.deliberationGroups && (
            <Chip
              icon={<Group sx={{ fontSize: '0.7rem' }} />}
              label={matter.deliberationGroups}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.65rem',
                height: 20,
                maxWidth: '100%',
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{
            mt: 0.5,
            display: 'block',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            fontWeight: 400,
          }}
        >
          {[matter.leadConsultant, matter.secondaryConsultant, matter.technician]
            .filter(consultant => consultant && consultant.trim() !== '')
            .join(', ')}
        </Typography>
      </CardContent>
    </Card>
  );

  if (isError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message || 'Failed to load calendar data'}
        </Alert>
        <Button variant="contained" onClick={handleRefresh} startIcon={<Refresh />}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Header */}
      <Paper elevation={1} sx={{ zIndex: 10 }}>
        <Toolbar sx={{
          gap: 2,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          py: 2,
          minHeight: 'auto',
          alignItems: 'center'
        }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: isMobile ? 1 : 0, minWidth: 200 }}
          >
            Jury Project Calendar
          </Typography>

          {/* Filters */}
          <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Types</InputLabel>
              <Select
                multiple
                value={filters.matterType}
                onChange={e => setFilters({ matterType: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
                input={<OutlinedInput label="Types" />}
                renderValue={(selected) => {
                  const maxVisible = 2;
                  const totalTypes = Object.keys(MATTER_TYPE_CONFIGS).length;

                  if (selected.length === 0) {
                    return <em>None selected</em>;
                  }

                  if (selected.length === totalTypes) {
                    return 'All types';
                  }

                  const visibleItems = selected.slice(0, maxVisible);
                  const remainingCount = selected.length - maxVisible;

                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      {visibleItems.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                      {remainingCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          and {remainingCount} other{remainingCount > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>
                  );
                }}
              >
                {Object.keys(MATTER_TYPE_CONFIGS).map(type => (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={filters.matterType.indexOf(type) > -1} />
                    <ListItemText primary={type} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Consultant</InputLabel>
              <Select
                value={filters.consultant}
                onChange={e => setFilters({ consultant: e.target.value })}
                label="Consultant"
              >
                <MenuItem value="All">All Consultants</MenuItem>
                {consultants.map(consultant => (
                  <MenuItem key={consultant} value={consultant}>
                    {consultant}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton onClick={handleRefresh} title="Refresh" disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} /> : <Refresh />}
            </IconButton>
          </Box>

          {/* Color Legend */}
          <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Date Types:
            </Typography>
            <Chip
              label="Due Date"
              size="small"
              sx={{
                fontSize: '0.6rem',
                height: 20,
                fontWeight: 600,
                bgcolor: DATE_TYPE_COLORS.DUE.color,
                color: '#ffffff',
              }}
            />
            <Chip
              label="Span Date"
              size="small"
              sx={{
                fontSize: '0.6rem',
                height: 20,
                fontWeight: 600,
                bgcolor: DATE_TYPE_COLORS.SPAN.color,
                color: '#ffffff',
              }}
            />
            <Chip
              label="Other Dates"
              size="small"
              sx={{
                fontSize: '0.6rem',
                height: 20,
                fontWeight: 600,
                bgcolor: DATE_TYPE_COLORS.ADDITIONAL.color,
                color: '#000000',
              }}
            />
          </Box>

          {/* Navigation */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ml: 'auto'
          }}>
            <IconButton onClick={goToPreviousMonth} size="small">
              <ChevronLeft />
            </IconButton>

            <Button onClick={goToToday} variant="outlined" size="small" startIcon={<Today />}>
              Today
            </Button>

            <IconButton onClick={goToNextMonth} size="small">
              <ChevronRight />
            </IconButton>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1
                },
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
              onClick={() => setShowDatePicker(true)}
            >
              <Typography variant="h6" sx={{ minWidth: 150, textAlign: 'center' }}>
                {formatDate(currentDate, 'MMMM yyyy')}
              </Typography>
              <Event fontSize="small" color="action" />
            </Box>
          </Box>

          {/* User Profile */}
          <Box sx={{
            ml: 2,
            borderLeft: 1,
            borderColor: 'divider',
            pl: 2
          }}>
            <IconButton
              onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
              size="small"
              title="Profile"
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </Paper>

      {/* Calendar Grid */}
      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Day Headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <Paper
                key={day}
                sx={{
                  p: 1,
                  textAlign: 'center',
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: '8px 8px 0 0',
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {day}
                </Typography>
              </Paper>
            ))}
          </Box>

          {/* Calendar Weeks */}
          {calendarWeeks.map((week, weekIndex) => (
            <Box key={`week-${weekIndex}`} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {week.map((day, dayIndex) => {
                const dayEvents = getEventsForDate(day);
                const isCurrentDay = isToday(day);
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();

                return (
                  <Paper
                    key={`${day.toISOString()}-${weekIndex}-${dayIndex}`}
                    sx={{
                      p: 1,
                      minHeight: 120,
                      cursor: 'pointer',
                      bgcolor: !isCurrentMonth ? 'grey.50' : 'white',
                      border: 1,
                      borderColor: isCurrentDay ? 'primary.main' : 'divider',
                      borderWidth: isCurrentDay ? 2 : 1,
                      '&:hover': { bgcolor: 'action.hover' },
                      opacity: !isCurrentMonth ? 0.6 : 1,
                      borderRadius: 0,
                      overflow: 'hidden',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                        minWidth: 0,
                        pr: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={isCurrentDay ? 'bold' : 'normal'}
                        color={isCurrentDay ? 'primary.main' : 'text.primary'}
                      >
                        {formatDate(day, 'd')}
                      </Typography>
                      {dayEvents.length > 0 && (
                        <Badge badgeContent={dayEvents.length} color="primary" />
                      )}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      {dayEvents.map(event => renderMatterCard(event.matter, true, `${event.matter.id}-${event.dateType}-${event.date.getTime()}`, event))}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Matter Detail Dialog */}
      <Dialog
        open={!!selectedMatter}
        onClose={() => {
          setSelectedMatter(null);
          setSelectedCalendarDate(null);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {selectedMatter && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">{selectedMatter.displayName}</Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {selectedMatter.client}
                </Typography>
              </Box>
              <Chip
                label={selectedMatter.originalType || selectedMatter.type}
                sx={{
                  bgcolor: getTypeColor(selectedMatter.type),
                  color: 'white',
                }}
              />
              <IconButton
                component="a"
                href={BIGTIME_LINKS.GENERATE_URL(selectedMatter.bigtimeProjectId || selectedMatter.id)}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in BigTime"
                color="primary"
              >
                <OpenInNew />
              </IconButton>
            </DialogTitle>

            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography>
                      <strong>Location:</strong> {selectedMatter.caseLocation}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Person fontSize="small" color="action" />
                    <Typography>
                      <strong>Lead Consultant:</strong> {selectedMatter.leadConsultant}
                    </Typography>
                  </Box>

                  {selectedMatter.secondaryConsultant && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Person fontSize="small" color="action" />
                      <Typography>
                        <strong>Secondary:</strong> {selectedMatter.secondaryConsultant}
                      </Typography>
                    </Box>
                  )}

                  {selectedMatter.technician && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Person fontSize="small" color="action" />
                      <Typography>
                        <strong>Technician:</strong> {selectedMatter.technician}
                      </Typography>
                    </Box>
                  )}

                  {selectedMatter.focusGroupAddress && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Person fontSize="small" color="action" />
                      <Typography>
                        <strong>Focus Group Address:</strong> {selectedMatter.focusGroupAddress}
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  {selectedCalendarDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Event fontSize="small" color="action" />
                      <Typography>
                        <strong>Date:</strong> {formatDate(selectedCalendarDate, 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                  )}

                  {selectedMatter.trialPresentation && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Gavel fontSize="small" color="action" />
                      <Typography>
                        <strong>Trial Presentation:</strong> {selectedMatter.trialPresentation}
                      </Typography>
                    </Box>
                  )}

                  {selectedMatter.deliberationGroups && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Group fontSize="small" color="action" />
                      <Typography>
                        <strong>Groups:</strong> {selectedMatter.deliberationGroups}
                      </Typography>
                    </Box>
                  )}

                  {selectedMatter.reportTurnaround && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Group fontSize="small" color="action" />
                      <Typography>
                        <strong>Report Turnaround:</strong> {selectedMatter.reportTurnaround}
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {selectedMatter.notes && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Notes:</strong> {selectedMatter.notes}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => {
                setSelectedMatter(null);
                setSelectedCalendarDate(null);
              }}>Close</Button>
              <Button
                variant="contained"
                component="a"
                href={BIGTIME_LINKS.GENERATE_URL(selectedMatter.bigtimeProjectId || selectedMatter.id)}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNew />}
              >
                Open in BigTime
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Date Picker Dialog */}
      <Dialog open={showDatePicker} onClose={() => setShowDatePicker(false)}>
        <DialogTitle>Jump to Date</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <DatePicker
              value={currentDate}
              onChange={date => date && setCurrentDate(date)}
              slotProps={{
                textField: { fullWidth: true },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDatePicker(false)}>Cancel</Button>
          <Button onClick={() => setShowDatePicker(false)} variant="contained">
            Go to Date
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={() => setProfileMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {account?.name || account?.username}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setProfileMenuAnchor(null);
            instance.logoutRedirect();
          }}
          sx={{ gap: 1 }}
        >
          <ExitToApp fontSize="small" />
          Sign Out
        </MenuItem>
      </Menu>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Loading Overlay */}
      <LoadingOverlay open={isLoading} message="Loading calendar data..." />
    </Box>
  );
};

export default CalendarDashboard;
